// If IndexedDB isn't available, we'll fall back to sessionStorage.
// Note that this will have considerable performance and storage
// side-effects (all data will be serialized on save and only data that
// can be converted to a string via `JSON.stringify()` will be saved).
// eslint-disable-next-line import/extensions

function isSessionStorageValid() {
    try {
        return (
            typeof sessionStorage !== 'undefined' &&
            'setItem' in sessionStorage &&
            // in IE8 typeof sessionStorage.setItem === 'object'
            !!sessionStorage.setItem
        );
    } catch (e) {
        return false;
    }
}

function executeCallback(promise: any, callback: any) {
    if (callback) {
        promise.then(
            (result: any) => {
                callback(null, result);
            },
            (error: any) => {
                callback(error);
            },
        );
    }
}

function _getKeyPrefix(options: any, defaultConfig: any) {
    let keyPrefix = `${options.name}/`;

    if (options.storeName !== defaultConfig.storeName) {
        keyPrefix += `${options.storeName}/`;
    }
    return keyPrefix;
}

// Check if sessionStorage throws when saving an item
function checkIfLocalStorageThrows() {
    const localStorageTestKey = '_localforage_support_test';

    try {
        sessionStorage.setItem(localStorageTestKey, 'true');
        sessionStorage.removeItem(localStorageTestKey);

        return false;
    } catch (e) {
        return true;
    }
}

// Check if sessionStorage is usable and allows to save an item
// This method checks if sessionStorage is usable in Safari Private Browsing
// mode, or in any other case where the available quota for sessionStorage
// is 0 and there wasn't any saved items yet.
function _isLocalStorageUsable() {
    return !checkIfLocalStorageThrows() || sessionStorage.length > 0;
}

// Config the sessionStorage backend, using options set in the config.
function _initStorage(options: any) {
    const self = this;
    const dbInfo: any = {};
    if (options) {
        // eslint-disable-next-line guard-for-in
        for (const i in options) {
            dbInfo[i] = options[i];
        }
    }

    dbInfo.keyPrefix = _getKeyPrefix(options, self._defaultConfig);

    if (!_isLocalStorageUsable()) {
        return Promise.reject();
    }

    self._dbInfo = dbInfo;

    return Promise.resolve();
}

// Remove all keys from the datastore, effectively destroying all data in
// the app's key/value store!
function clear(callback: any) {
    const self = this;
    const promise = self.ready().then(() => {
        const keyPrefix = self._dbInfo.keyPrefix;

        for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
            const session_key = sessionStorage.key(i) || '';

            if (session_key.indexOf(keyPrefix) === 0) {
                sessionStorage.removeItem(session_key);
            }
        }
    });

    executeCallback(promise, callback);
    return promise;
}

// Retrieve an item from the store. Unlike the original async_storage
// library in Gaia, we don't modify return values at all. If a key's value
// is `undefined`, we pass that value to the callback function.
function getItem(session_key: string, callback: any) {
    const self = this;

    const promise = self.ready().then(() => {
        const dbInfo = self._dbInfo;
        let result = sessionStorage.getItem(dbInfo.keyPrefix + session_key);

        // If a result was found, parse it from the serialized
        // string into a JS object. If result isn't truthy, the key
        // is likely undefined and we'll pass it straight to the
        // callback.
        if (result) {
            try {
                result = JSON.parse(result);
            } catch (error) {
                //
            }
        }

        return result;
    });

    executeCallback(promise, callback);
    return promise;
}

// Iterate over all items in the store.
function iterate(iterator: any, callback: any) {
    const self = this;

    // eslint-disable-next-line consistent-return
    const promise = self.ready().then(() => {
        const dbInfo = self._dbInfo;
        const keyPrefix = dbInfo.keyPrefix;
        const keyPrefixLength = keyPrefix.length;
        const len: any = sessionStorage.length;

        // We use a dedicated iterator instead of the `i` variable below
        // so other keys we fetch in sessionStorage aren't counted in
        // the `iterationNumber` argument passed to the `iterate()`
        // callback.
        //
        // See: github.com/mozilla/localForage/pull/435#discussion_r38061530
        let iterationNumber = 1;

        for (let i = 0; i < len; i += 1) {
            const session_key = sessionStorage.key(i) || '';
            if (session_key.indexOf(keyPrefix) !== 0) {
                // eslint-disable-next-line no-continue
                continue;
            }
            let value = sessionStorage.getItem(session_key);

            // If a result was found, parse it from the serialized
            // string into a JS object. If result isn't truthy, the
            // key is likely undefined and we'll pass it straight
            // to the iterator.
            // if (value) {
            //     value = dbInfo.serializer.deserialize(value);
            // }

            value = iterator(
                value,
                session_key.substring(keyPrefixLength),
                iterationNumber += 1,
            );

            // eslint-disable-next-line no-void
            if (value !== void 0) {
                return value;
            }
        }
    });

    executeCallback(promise, callback);
    return promise;
}

// Same as sessionStorage's key() method, except takes a callback.
function key(n: any, callback: any) {
    const self = this;
    const promise = self.ready().then(() => {
        const dbInfo = self._dbInfo;
        let result;
        try {
            result = sessionStorage.key(n);
        } catch (error) {
            result = null;
        }

        // Remove the prefix from the key, if a key is found.
        if (result) {
            result = result.substring(dbInfo.keyPrefix.length);
        }

        return result;
    });

    executeCallback(promise, callback);
    return promise;
}

function keys(callback: any) {
    const self = this;
    const promise = self.ready().then(() => {
        const dbInfo = self._dbInfo;
        const len = sessionStorage.length;
        const key_list: string[] = [];

        for (let i = 0; i < len; i += 1) {
            const itemKey = sessionStorage.key(i) || '';
            if (itemKey.indexOf(dbInfo.keyPrefix) === 0) {
                key_list.push(itemKey.substring(dbInfo.keyPrefix.length));
            }
        }

        return key_list;
    });

    executeCallback(promise, callback);
    return promise;
}

// Supply the number of keys in the datastore to the callback function.
function length(callback: any) {
    const self = this;
    const promise = self.keys().then((_keys: any) => {
        return _keys.length;
    });

    executeCallback(promise, callback);
    return promise;
}

// Remove an item from the store, nice and simple.
function removeItem(session_key: string, callback: any) {
    const self = this;

    const promise = self.ready().then(() => {
        const dbInfo = self._dbInfo;
        sessionStorage.removeItem(dbInfo.keyPrefix + session_key);
    });

    executeCallback(promise, callback);
    return promise;
}

// Set a key's value and run an optional callback once the value is set.
// Unlike Gaia's implementation, the callback function is passed the value,
// in case you want to operate on that value only after you're sure it
// saved, or something like that.
function setItem(session_key: string, value: any, callback: any) {
    const self = this;

    const promise = self.ready().then(() => {
        // Convert undefined values to null.
        // https://github.com/mozilla/localForage/pull/42
        if (value === undefined) {
            value = null;
        }

        // Save the original value to pass to the callback.
        const originalValue = value || '';

        return new Promise((resolve, reject) => {
            const dbInfo = self._dbInfo;
            try {
                sessionStorage.setItem(dbInfo.keyPrefix + session_key, JSON.stringify(originalValue));
                resolve(originalValue);
            } catch (e: any) {
                // sessionStorage capacity exceeded.
                // TODO: Make this a specific error/event.
                // if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
                //     reject(e);
                // }
                reject(e);
            }
        });
    });

    executeCallback(promise, callback);
    return promise;
}

function dropInstance(options: any, callback: any) {
    options = (typeof options !== 'function' && options) || {};
    if (!options.name) {
        const currentConfig = this.config();
        options.name = options.name || currentConfig.name;
        options.storeName = options.storeName || currentConfig.storeName;
    }

    const self = this;
    let promise;
    if (!options.name) {
        promise = Promise.reject(Error('Invalid arguments'));
    } else {
        promise = new Promise((resolve) => {
            if (!options.storeName) {
                resolve(`${options.name}/`);
            } else {
                resolve(_getKeyPrefix(options, self._defaultConfig));
            }
        }).then((keyPrefix: any) => {
            for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
                const session_key = sessionStorage.key(i) || '';

                if (session_key.indexOf(keyPrefix) === 0) {
                    sessionStorage.removeItem(session_key);
                }
            }
        });
    }

    executeCallback(promise, callback);
    return promise;
}

const sessionStorageWrapper = {
    _driver: 'sessionStorageWrapper',
    _initStorage,
    _support: isSessionStorageValid(),
    iterate,
    getItem,
    setItem,
    removeItem,
    clear,
    length,
    key,
    keys,
    dropInstance,
};

export default sessionStorageWrapper;
